import { QualityLevel, EffectsConfig } from './EffectsManager';

export interface DeviceCapabilities {
  isMobile: boolean;
  isTablet: boolean;
  gpuTier: 'low' | 'medium' | 'high';
  memoryGB: number;
  supportsWebGL2: boolean;
  maxTextureSize: number;
}

export interface QualitySettings {
  particleCount: number;
  shaderComplexity: 'simple' | 'standard' | 'complex' | 'ultra';
  textureQuality: number; // 0.25, 0.5, 1.0, 2.0
  effectDistance: number; // render distance multiplier
  postProcessing: boolean;
  shadows: boolean;
  antialiasing: boolean;
  bloomIntensity: number;
}

export class QualityManager {
  private config: EffectsConfig;
  private deviceCapabilities: DeviceCapabilities;
  private qualitySettings: Record<QualityLevel, QualitySettings>;
  private currentQuality: QualityLevel = 'medium';
  
  // Performance monitoring
  private frameTimeHistory: number[] = [];
  private memoryUsageHistory: number[] = [];
  private targetFrameTime: number;
  
  constructor(config: EffectsConfig) {
    this.config = config;
    this.targetFrameTime = 1000 / config.targetFPS;
    
    this.deviceCapabilities = this.detectDeviceCapabilities();
    this.qualitySettings = this.initializeQualitySettings();
    
    this.startMemoryMonitoring();
  }
  
  private detectDeviceCapabilities(): DeviceCapabilities {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
    
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    
    const isTablet = /iPad|Android/i.test(navigator.userAgent) && 
      window.innerWidth > 768;
    
    // Estimate GPU tier based on renderer info
    let gpuTier: 'low' | 'medium' | 'high' = 'medium';
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
        
        // Simple GPU tier detection
        if (renderer.includes('Intel') || renderer.includes('integrated')) {
          gpuTier = 'low';
        } else if (renderer.includes('NVIDIA') || renderer.includes('AMD') || renderer.includes('Radeon')) {
          gpuTier = 'high';
        }
      }
    }
    
    // Estimate memory (rough approximation)
    const memoryGB = (navigator as any).deviceMemory || (isMobile ? 2 : 8);
    
    const maxTextureSize = gl ? gl.getParameter(gl.MAX_TEXTURE_SIZE) : 2048;
    
    return {
      isMobile,
      isTablet,
      gpuTier,
      memoryGB,
      supportsWebGL2: !!canvas.getContext('webgl2'),
      maxTextureSize
    };
  }
  
  private initializeQualitySettings(): Record<QualityLevel, QualitySettings> {
    return {
      low: {
        particleCount: 500,
        shaderComplexity: 'simple',
        textureQuality: 0.5,
        effectDistance: 0.5,
        postProcessing: false,
        shadows: false,
        antialiasing: false,
        bloomIntensity: 0
      },
      medium: {
        particleCount: 2000,
        shaderComplexity: 'standard',
        textureQuality: 1.0,
        effectDistance: 0.75,
        postProcessing: false,
        shadows: false,
        antialiasing: true,
        bloomIntensity: 0.3
      },
      high: {
        particleCount: 5000,
        shaderComplexity: 'complex',
        textureQuality: 1.0,
        effectDistance: 1.0,
        postProcessing: true,
        shadows: true,
        antialiasing: true,
        bloomIntensity: 0.5
      },
      ultra: {
        particleCount: 10000,
        shaderComplexity: 'ultra',
        textureQuality: 2.0,
        effectDistance: 1.5,
        postProcessing: true,
        shadows: true,
        antialiasing: true,
        bloomIntensity: 0.8
      }
    };
  }
  
  public getDefaultQuality(): QualityLevel {
    const { isMobile, gpuTier, memoryGB } = this.deviceCapabilities;
    
    if (isMobile) {
      return memoryGB >= 4 ? 'medium' : 'low';
    }
    
    switch (gpuTier) {
      case 'low':
        return 'medium';
      case 'high':
        return memoryGB >= 8 ? 'high' : 'medium';
      default:
        return 'medium';
    }
  }
  
  public setCurrentQuality(quality: QualityLevel): void {
    this.currentQuality = quality;
  }
  
  public getCurrentQuality(): QualityLevel {
    return this.currentQuality;
  }
  
  public getQualitySettings(quality?: QualityLevel): QualitySettings {
    return this.qualitySettings[quality || this.currentQuality];
  }
  
  public getDeviceCapabilities(): DeviceCapabilities {
    return { ...this.deviceCapabilities };
  }
  
  public shouldReduceQuality(frameTime: number): boolean {
    if (!this.config.autoQualityAdjustment) return false;
    
    this.frameTimeHistory.push(frameTime);
    if (this.frameTimeHistory.length > 30) {
      this.frameTimeHistory.shift();
    }
    
    if (this.frameTimeHistory.length < 10) return false;
    
    const averageFrameTime = this.frameTimeHistory.reduce((a, b) => a + b, 0) / this.frameTimeHistory.length;
    return averageFrameTime > this.targetFrameTime * 1.2; // 20% over target
  }
  
  public shouldIncreaseQuality(_frameTime: number): boolean {
    if (!this.config.autoQualityAdjustment) return false;
    
    if (this.frameTimeHistory.length < 30) return false;
    
    const averageFrameTime = this.frameTimeHistory.reduce((a, b) => a + b, 0) / this.frameTimeHistory.length;
    return averageFrameTime < this.targetFrameTime * 0.8; // 20% under target
  }
  
  public getParticleCountForQuality(baseCount: number, quality?: QualityLevel): number {
    const settings = this.getQualitySettings(quality);
    return Math.floor(baseCount * (settings.particleCount / 2000)); // normalized to medium
  }
  
  public getTextureResolution(baseResolution: number, quality?: QualityLevel): number {
    const settings = this.getQualitySettings(quality);
    return Math.floor(baseResolution * settings.textureQuality);
  }
  
  public getEffectDistance(baseDistance: number, quality?: QualityLevel): number {
    const settings = this.getQualitySettings(quality);
    return baseDistance * settings.effectDistance;
  }
  
  public shouldUseComplexShader(quality?: QualityLevel): boolean {
    const settings = this.getQualitySettings(quality);
    return settings.shaderComplexity !== 'simple';
  }
  
  public shouldUsePostProcessing(quality?: QualityLevel): boolean {
    const settings = this.getQualitySettings(quality);
    return settings.postProcessing;
  }
  
  public getBloomIntensity(quality?: QualityLevel): number {
    const settings = this.getQualitySettings(quality);
    return settings.bloomIntensity;
  }
  
  private startMemoryMonitoring(): void {
    if ('memory' in performance) {
      const updateMemoryStats = () => {
        const memInfo = (performance as any).memory;
        if (memInfo) {
          const usagePercent = memInfo.usedJSHeapSize / memInfo.jsHeapSizeLimit;
          this.memoryUsageHistory.push(usagePercent);
          
          if (this.memoryUsageHistory.length > 60) {
            this.memoryUsageHistory.shift();
          }
        }
        
        setTimeout(updateMemoryStats, 1000); // Check every second
      };
      
      updateMemoryStats();
    }
  }
  
  public getMemoryPressure(): number {
    if (this.memoryUsageHistory.length === 0) return 0;
    
    const recentUsage = this.memoryUsageHistory.slice(-10);
    return recentUsage.reduce((a, b) => a + b, 0) / recentUsage.length;
  }
  
  public isMemoryConstrained(): boolean {
    return this.getMemoryPressure() > 0.8; // 80% memory usage
  }
  
  public getOptimalQualityForDevice(): QualityLevel {
    const memoryPressure = this.getMemoryPressure();
    const defaultQuality = this.getDefaultQuality();
    
    if (memoryPressure > 0.9) {
      return 'low';
    } else if (memoryPressure > 0.7) {
      return defaultQuality === 'ultra' ? 'high' : defaultQuality;
    }
    
    return defaultQuality;
  }
  
  public getPerformanceReport(): {
    deviceCapabilities: DeviceCapabilities;
    currentQuality: QualityLevel;
    memoryPressure: number;
    recommendedQuality: QualityLevel;
    frameTimeHistory: number[];
  } {
    return {
      deviceCapabilities: this.deviceCapabilities,
      currentQuality: this.currentQuality,
      memoryPressure: this.getMemoryPressure(),
      recommendedQuality: this.getOptimalQualityForDevice(),
      frameTimeHistory: [...this.frameTimeHistory]
    };
  }
  
  public dispose(): void {
    this.frameTimeHistory = [];
    this.memoryUsageHistory = [];
  }
}